import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, Building2, Crown } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AnalyticsPage from './AnalyticsPage';
import { environmentsFor, defaultTabFor, envForTab } from '../analyticsTabs';
import { hasAdminAccess, hasSuperAdminAccess } from "../../../utils/roleAccess";

const STORAGE_KEY = 'analytics.environment';
// Executive had no entry and fell through to the CRM icon, so the two sat side
// by side wearing the same badge — part of why they read as the same thing.
const ENV_ICONS = { portal: Building2, crm: BarChart3, executive: Crown };

/**
 * Analytics shell — picks the environment, AnalyticsPage renders its tabs.
 *
 * Analytics had grown into one strip of 14 tabs mixing two different products:
 * buyer-portal analytics (how consumers use the platform) and CRM analytics
 * (how the agency works, and whether that work makes money). They answer
 * different questions for different audiences, and the single strip was already
 * overflowing, so they are split here into a permanent segmented control.
 *
 * The CRM environment is admin-only, matching the existing gate on the Agents
 * tab. A plain agent sees only Portal, with no segmented control at all.
 *
 * There are three environments, and the order is Portal | CRM | Executive on
 * Christian's instruction (16 Aug 2026). They are meant to read as increasing
 * altitude: the consumer platform, then the agency agent by agent, then the
 * agency as one number. Anything that appears on two of them is a bug in the
 * tab config, not a convenience (see analyticsTabs.js).
 */
const AnalyticsDashboardWrapper = () => {
    const { user } = useSelector((state) => state.auth);
    // Cut-down non-admin view (agents AND PAs) — see AnalyticsPage.js. A PA is
    // not an 'agent' by role, so gate on "not an admin" so PAs get the same
    // Property/Buyer/Renter-only analytics as agents (CG, 7 Sep 2026,
    // #12982366802), not the full admin environment set.
    const isAgent = !hasAdminAccess(user);
    // A PLAIN ADMIN is false here. CRM Analytics and Executive are the agency
    // principal's, not every admin's (Christian, 31 Aug 2026: "Cut CRM analytics
    // & Executive from other admins"), so an admin is left with Portal Analytics
    // - which is the "some access to the analytics but not all of it" that now
    // separates an admin from an agent.
    const isSuperAdmin = hasSuperAdminAccess(user);
    const location = useLocation();

    const environments = useMemo(
        () => environmentsFor({ isAgent, isSuperAdmin }),
        [isAgent, isSuperAdmin]
    );

    // Resolution order: the tab named in ?view= (so a deep link from a contact
    // profile lands in the right environment), then the last environment used,
    // then Portal.
    const [environment, setEnvironment] = useState(() => {
        const view = new URLSearchParams(location.search).get('view');
        const fromView = view && envForTab(view);
        if (fromView && environments.some((e) => e.id === fromView)) return fromView;
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && environments.some((e) => e.id === stored)) return stored;
        return 'portal';
    });

    // The tab within the environment. Changing environment resets it to that
    // environment's first visible tab.
    const [activeView, setActiveView] = useState(() => {
        const view = new URLSearchParams(location.search).get('view');
        if (view && envForTab(view)) return view;
        return null;
    });

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, environment);
    }, [environment]);

    const handleEnvironmentChange = (id) => {
        if (id === environment) return;
        setEnvironment(id);
        setActiveView(defaultTabFor(id, { isAgent, isSuperAdmin }));
    };

    // An admin whose saved environment was 'crm' (or whose ?view= names a CRM
    // tab) can no longer open it. Fall back to their first allowed environment
    // rather than rendering a blank page against an environment that is gone.
    const current = environments.find((e) => e.id === environment) || environments[0];

    useEffect(() => {
        if (!environments.length) return;
        if (environments.some((e) => e.id === environment)) return;
        const fallback = environments[0].id;
        setEnvironment(fallback);
        setActiveView(defaultTabFor(fallback, { isAgent, isSuperAdmin }));
    }, [environments, environment, isAgent, isSuperAdmin]);

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="w-full py-6 px-3 sm:px-4 md:px-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Analytics</h1>
                            <p className="text-sm text-gray-500 mt-1">{current?.description}</p>
                        </div>
                    </div>

                    {/* Environment selector — hidden entirely when there's only one */}
                    {environments.length > 1 && (
                        <div className="flex items-center gap-2 overflow-x-auto">
                            {environments.map((env) => {
                                const Icon = ENV_ICONS[env.id] || BarChart3;
                                const isActive = environment === env.id;
                                return (
                                    <button
                                        key={env.id}
                                        onClick={() => handleEnvironmentChange(env.id)}
                                        className={`flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${isActive
                                            ? 'bg-[#55C5D0] text-white'
                                            : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                    >
                                        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                                        <span className="whitespace-nowrap">{env.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Dashboard Content */}
            <div>
                <AnalyticsPage
                    environment={environment}
                    activeView={activeView}
                    onViewChange={setActiveView}
                />
            </div>
        </div>
    );
};

export default AnalyticsDashboardWrapper;
